import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronLeft, ChevronRight, Expand, Play, X } from 'lucide-react'
import type { MediaAsset } from '../content/types'
import type { UiCopy } from '../content/types'

interface GalleryProps {
  items: MediaAsset[]
  ui: UiCopy
}

function MediaImage({ item, eager = false, lightbox = false }: { item: MediaAsset; eager?: boolean; lightbox?: boolean }) {
  if (item.kind === 'youtube') {
    return (
      <span className="youtube-placeholder" aria-hidden="true">
        <Play size={30} fill="currentColor" />
        <span>{item.caption}</span>
      </span>
    )
  }
  if (item.kind === 'video') {
    return (
      <video
        src={item.src}
        poster={item.poster}
        muted
        autoPlay
        loop={!lightbox}
        playsInline
        controls={lightbox}
        preload={lightbox ? 'auto' : 'metadata'}
        aria-label={item.alt}
      />
    )
  }
  return (
    <picture>
      {item.webp && <source srcSet={item.webp} type="image/webp" />}
      <img src={item.src} alt={item.alt} loading={eager ? 'eager' : 'lazy'} decoding="async" />
    </picture>
  )
}

export function Gallery({ items, ui }: GalleryProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const triggerRef = useRef<HTMLElement | null>(null)
  const galleryRef = useRef<HTMLDivElement>(null)
  const activeItem = activeIndex === null ? null : items[activeIndex]

  const close = useCallback(() => setActiveIndex(null), [])
  const previous = useCallback(() => setActiveIndex((value) => value === null ? null : (value - 1 + items.length) % items.length), [items.length])
  const next = useCallback(() => setActiveIndex((value) => value === null ? null : (value + 1) % items.length), [items.length])
  const scrollGallery = useCallback((direction: -1 | 1) => {
    galleryRef.current?.scrollBy({ left: direction * galleryRef.current.clientWidth * 0.8, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    if (activeIndex === null) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
      if (event.key === 'ArrowLeft') previous()
      if (event.key === 'ArrowRight') next()
      if (event.key === 'Tab') {
        const modal = document.querySelector<HTMLElement>('.lightbox-panel')
        const focusable = modal?.querySelectorAll<HTMLElement>('button, a, iframe, [tabindex]:not([tabindex="-1"])')
        if (!focusable?.length) return
        const first = focusable[0]!
        const last = focusable[focusable.length - 1]!
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
      triggerRef.current?.focus()
    }
  }, [activeIndex, close, next, previous])

  if (!items.length) return null

  return (
    <>
      <div className="gallery-shell">
        <div className="gallery-scroll-controls">
          <button type="button" onClick={() => scrollGallery(-1)} aria-label={ui.previous}><ChevronLeft /></button>
          <button type="button" onClick={() => scrollGallery(1)} aria-label={ui.next}><ChevronRight /></button>
        </div>
        <div
          className="gallery"
          aria-label={ui.products}
          ref={galleryRef}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') scrollGallery(-1)
            if (event.key === 'ArrowRight') scrollGallery(1)
          }}
        >
          {items.map((item, index) => (
            <button
              key={`${item.src}-${index}`}
              className={`gallery-item ${item.kind === 'video' || item.kind === 'youtube' ? 'video-item' : ''}`}
              onClick={(event) => { triggerRef.current = event.currentTarget; setActiveIndex(index) }}
              aria-label={`${ui.openImage}: ${item.caption ?? item.alt}`}
            >
              <MediaImage item={item} />
              <span className="gallery-zoom" aria-hidden="true"><Expand size={16} /></span>
            </button>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {activeItem && (
          <motion.div className="lightbox" role="dialog" aria-modal="true" aria-label={activeItem.caption ?? activeItem.alt} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={(event) => { if (event.target === event.currentTarget) close() }}>
            <motion.div className="lightbox-panel" initial={{ opacity: 0, y: 18, scale: 0.985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.985 }}>
              <button ref={closeRef} className="lightbox-close" onClick={close} aria-label={ui.close}><X /></button>
              <div className="lightbox-media">
                {activeItem.kind === 'youtube' ? (
                  <iframe src={`https://www.youtube-nocookie.com/embed/${activeItem.src}?autoplay=1`} title={activeItem.alt} allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                ) : <MediaImage item={activeItem} eager lightbox />}
              </div>
              <div className="lightbox-footer">
                <button onClick={previous} aria-label={ui.previous}><ChevronLeft /></button>
                <p>{activeItem.caption ?? activeItem.alt}<span>{(activeIndex ?? 0) + 1} / {items.length}</span></p>
                <button onClick={next} aria-label={ui.next}><ChevronRight /></button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
